'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Home, Search, MessageCircle, User, Bookmark } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


const PROTECTED_NAV = ['/ai-chat-page', '/profile-page'];

const navItems = [
  { key: 'nav-home', label: 'Home', icon: Home, href: '/' },
  { key: 'nav-search', label: 'Search', icon: Search, href: '/search-results-page' },
  { key: 'nav-saved', label: 'Saved', icon: Bookmark, href: '/saved-passages' },
  { key: 'nav-chat', label: 'AI Chat', icon: MessageCircle, href: '/ai-chat-page' },
  { key: 'nav-profile', label: 'Profile', icon: User, href: '/profile-page' },
];

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();

  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const initialHeight = window.innerHeight;
    const onResize = () => {
      if (window.visualViewport) {
        setKeyboardOpen(window.visualViewport.height < initialHeight - 100);
      }
    };
    window.visualViewport?.addEventListener('resize', onResize);
    return () => window.visualViewport?.removeEventListener('resize', onResize);
  }, []);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (!user && PROTECTED_NAV.includes(href)) {
      e.preventDefault();
      router.push('/sign-in');
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bottom-nav-safe"
      style={{ background: 'var(--card)', borderTop: '1px solid var(--border)', display: keyboardOpen ? 'none' : undefined }}>
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
        {navItems?.map(({ key, label, icon: Icon, href }) => {
          const isActive = pathname === href || (href !== '/' && pathname?.startsWith(href));
          return (
            <Link
              key={key}
              href={href}
              onClick={(e) => handleNavClick(e, href)}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200 group"
              style={{
                color: isActive ? 'var(--accent)' : 'var(--muted-foreground)',
              }}
            >
              <div className={`p-1.5 rounded-lg transition-all duration-200 ${isActive ? 'bg-accent/10' : 'group-hover:bg-muted'}`}>
                <Icon
                  size={20}
                  strokeWidth={isActive ? 2.5 : 1.8}
                />
              </div>
              <span className={`text-[10px] font-${isActive ? '600' : '500'} tracking-wide`}>
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}